import { useEffect, useState } from 'react';
import {FUND, ETF, CEF} from 'src/Api';

const priceFrequencyMapping = {
    "2$": "Twice a year",
    "3$": "Three times a year",
    "4$": "Four times a year",
    "A$": "Annually",
    "a$": "Semi-Annually",
    "B$": "Bi-Monthly",
    "D$": "Daily",
    "I$": "Irregular",
    "M$": "Monthly",
    "m$": "Semi-Monthly",
    "N$": "None",
    "Q$": "Quarterly",
    "T$": "Triannual",
    "W$": "Weekly"
};

const OPEN_PRICE = 'openPrice';
const CLOSE_PRICE = 'closePrice';
const DAY_HIGH_PRICE = 'dayHighPrice';
const YEAR_HIGH_PRICE = 'yearHighPrice';
const YEAR_LOW_PRICE = 'yearLowPrice';
const CUMULATIVE_VOLUME = 'cumulativeVolume';
const DIVIDEND_YIELD = 'dividendYield';
const DIVIDEND_EXDATE = 'dividendExDate';
const DIVIDEND_PAYDATE = 'dividendPayDate';
const ONGOING_CHARGE = 'ongoingCharge';
const ISIN = 'isin';
const MARKET_CAP = 'marketCap';
const UCITS = 'ucits';
const YIELD = 'yield';
const ESTIMATED_NAV_PRICE = 'estimatedNavPrice';
const PRICING_FREQUENCY = 'pricingFrequency';
const PREMIUM_DISCOUNT = 'premiumDiscount';
const PE_RATIO = 'peRatio';
const REPLICATION_METHOD = 'replicationMethod';
const INSTRUMENT_TYPE = 'instrumentType';
const DEALING_CUT_OFF_TIME = 'dealingCutOffTime';
const INSTRUMENT_STRUCTURE = 'instrumentStructure';
const LATEST_NAV_PRICE = 'latestNavPrice';
const LATEST_NAV_DATE = 'latestNavDate';
const INCOME_FREQUENCY = 'incomeFrequency';

const quoteColumnsInitial = {
    CEF: [[
        [OPEN_PRICE, CLOSE_PRICE, DAY_HIGH_PRICE],
        [ESTIMATED_NAV_PRICE, YEAR_HIGH_PRICE, YEAR_LOW_PRICE]
    ], [
        [PREMIUM_DISCOUNT, MARKET_CAP, CUMULATIVE_VOLUME],
        [DIVIDEND_YIELD, ONGOING_CHARGE, ISIN]
    ]],
    Equity: [[
        [OPEN_PRICE, CLOSE_PRICE, DAY_HIGH_PRICE],
        [CUMULATIVE_VOLUME, YEAR_HIGH_PRICE, YEAR_LOW_PRICE]
    ], [
        [DIVIDEND_YIELD, MARKET_CAP],
        [PE_RATIO, ISIN]
    ]],
    ETF: [[
        [OPEN_PRICE, CLOSE_PRICE, DAY_HIGH_PRICE, INCOME_FREQUENCY],
        [YEAR_LOW_PRICE, YEAR_HIGH_PRICE, REPLICATION_METHOD]
    ], [
        [CUMULATIVE_VOLUME, INSTRUMENT_TYPE, UCITS],
        [DIVIDEND_YIELD, ONGOING_CHARGE, ISIN]
    ]],
    Fund: [[
        [PRICING_FREQUENCY, DEALING_CUT_OFF_TIME, INSTRUMENT_STRUCTURE],
        [YIELD, LATEST_NAV_PRICE, INCOME_FREQUENCY]
    ], [
        [ONGOING_CHARGE, LATEST_NAV_DATE],
        [ISIN, UCITS]
    ]]
}

const labelsTips = {
    category: {
        label: "Category",
        tip: "Category tip"
    },
    [CLOSE_PRICE]: {
        label: "Previous close",
        tip: "The price at the end of the previous trading day. Closing prices are used to track a share's performance over time.",
        etfTip: "The price at the end of the previous trading day. Closing prices are used to track the fund’s performance over time.",
        cefTip: "The price at the end of the previous trading day. Closing prices are used to track a trust’s performance over time.",
        size: "large"
    },
    [DAY_HIGH_PRICE]: {
        label: "Trade high",
        tip: "The highest price the share has traded at today (or the most recent trading day, if the market is currently closed). Comparing with the current price can give you an idea of the share's volatility.",
        etfTip: "The highest price the fund has traded at today (or the most recent trading day, if the market is currently closed). Comparing this with the current price can give you an idea of the fund’s volatility.",
        cefTip: "The highest price the trust has traded at today (or the most recent trading day, if the market is currently closed). Comparing this with the current price can give you an idea of the trust’s volatility.",
        size: "large"
    },
    [DEALING_CUT_OFF_TIME]: {
        label: "Dealing cut off time",
        tip: "This is the latest time you can buy or sell the fund for your order to be executed on the same business day. Any orders placed after the cut-off will be dealt at the earliest available time, which may be the same day or the next day the fund is priced. For more information on a fund’s dealing times please refer to the fund prospectus on our fund research pages.",
        size: "large"
    },
    [DIVIDEND_YIELD]: {
        label: "Dividend yield",
        tip: "The historical dividend per share divided by price. Can be used as an indication of the potential income to be earned from this share.",
        etfTip: "The historical dividend per share divided by price. Can be used as an indication of the potential income you could earn.",
        cefTip: "The target dividend per share divided by price. Can be used as an indication of the potential income you could earn from this trust.",
        size: "large"
    },
    [DIVIDEND_EXDATE]: {
        label: "Next ex-div date",
        tip: "The date by which you'll need to own the company's shares to qualify for this dividend payment. If you bought shares on or after this date you will not receive the dividend.",
        size: "large"
    },
    [DIVIDEND_PAYDATE]: {
        label: "Next payment date",
        tip: "The date the dividend is paid to qualifying shareholders.",
        size: "medium"
    },
    [ESTIMATED_NAV_PRICE]: {
        label: "Estimated NAV",
        tip: "Estimated NAV price tip",
        cefTip: "The estimated net asset value (NAV) per share. You can compare this with the trust's price to see if it’s trading at a premium or discount."
    },
    [INSTRUMENT_TYPE]: {
        label: "Type",
        tip: "Tells you whether it’s an exchange traded fund (ETF), exchange traded commodity (ETC) or exchange traded note (ETN).",
        size: "large"
    },
    [ISIN]: {
        label: "ISIN",
        tip: "'International Securities Identification Number' – a unique identifier you can use when searching for a specific investment.",
        etfTip: "International Securities Identification Number – a unique identifier you can use when searching for a specific investment.",
        cefTip: "International Securities Identification Number – a unique identifier you can use when searching for a specific investment.",
        size: "large"
    },
    [LATEST_NAV_DATE]: {
        label: "Latest actual NAV date",
        tip: "When the most recent net asset value (NAV) was published by the fund."
    },
    [LATEST_NAV_PRICE]: {
        label: "NAV",
        tip: "'Net asset value' – the value of the investments in a fund. For a unit trust or OEIC, NAV per share normally corresponds to the fund's market price.",
        size: "large"
    },
    [MARKET_CAP]: {
        label: "Market capitalisation",
        tip: "The total value of all outstanding shares. Can be a useful measure of the company's size.",
        cefTip: "The total value of all outstanding shares. Can be a useful measure of the trust’s size.",
        size: "large"
    },
    [ONGOING_CHARGE]: {
        label: "Ongoing charge",
        tip: "The % amount you'll pay for holding the fund, including the annual management fee. (This doesn't include additional costs such as transaction and performance fees).",
        etfTip: "The ongoing charge percentage amount you'll pay for holding the fund, including the annual management fee - which doesn't include additional costs such as transaction and performance fees. The ongoing charges you will pay aren’t taken from cash in your account, as these are already factored into the price of the investment itself.",
        cefTip: "The ongoing charge percentage amount you'll pay for holding the fund, including the annual management fee - which doesn't include additional costs such as transaction and performance fees. The ongoing charges you will pay aren’t taken from cash in your account, as these are already factored into the price of the investment itself.",
        fundTip: "The ongoing charge percentage amount you'll pay for holding the fund, including the annual management fee - which doesn't include additional costs such as transaction and performance fees. The ongoing charges you will pay aren’t taken from cash in your account, as these are already factored into the price of the investment itself.",
        size: "large"
    },
    [OPEN_PRICE]: {
        label: "Open",
        tip: "The price the share opened, at the start of today’s trading – letting you see how it has performed since. If the market is closed today, this will be the price at the start of the most recent trading day.",
        etfTip: "The price the fund opened at the start of today’s trading – letting you see how it has performed since. If the market is closed today, this will be the price at the start of the most recent trading day.",
        cefTip: "The price the trust opened at the start of today’s trading – letting you see how it’s performed since. If the market is closed today, this will be the price at the start of the most recent trading day.",
        size: "large"
    },
    [PE_RATIO]: {
        label: "P/E ratio",
        tip: "Price-to-earnings ratio. This is calculated by dividing the current share price by current earnings per share. P/E ratios are an indication of how investors value a company.",
        size: "large"
    },
    [PREMIUM_DISCOUNT]: {
        label: "Premium / Discount",
        tip: "Premium / Discount tip",
        cefTip: "The difference between the trust's price and it's estimated NAV."
    },
    [PRICING_FREQUENCY]: {
        label: "Pricing frequency",
        tip: "How often the fund publishes its net asset value (NAV)."
    },
    [UCITS]: {
        label: "UCITS",
        tip: "'Undertakings for Collective Investment in Transferable Securities' – a European Union directive that establishes the terms under which a fund domiciled in one EU member state can be marketed in all EU countries.",
        etfTip: "‘Undertakings for Collective Investment in Transferable Securities' – an EU directive that establishes the terms under which a fund domiciled in one EU member state can be marketed in all EU countries.",
        size: "large"
    },
    [CUMULATIVE_VOLUME]: {
        label: "Volume",
        tip: "The total number of shares traded today (or the most recent trading day, if the market is currently closed).",
        etfTip: "The total number of shares traded today (or the most recent trading day, if the market is currently closed).",
        cefTip: "The total number of the trust’s shares traded today (or the most recent trading day, if the market is currently closed).",
        size: "large"
    },
    [YEAR_HIGH_PRICE]: {
        label: "Year high",
        tip: "The highest price recorded over the past 52 weeks. You can compare this with the year low to gauge longer-term performance.",
        etfTip: "The highest price recorded over the past 52 weeks. You can compare this with the year low to gauge longer-term performance.",
        cefTip: "The highest price recorded over the past 52 weeks. You can compare this with the year low to gauge longer-term performance.",
        size: "large"
    },
    [YEAR_LOW_PRICE]: {
        label: "Year low",
        tip: "The lowest price recorded over the past 52 weeks. You can compare this with the year high to gauge longer-term performance.",
        etfTip: "The lowest price recorded over the past 52 weeks. You can compare this with the year high to gauge longer-term performance.",
        cefTip: "The lowest price recorded over the past 52 weeks. You can compare this with the year high to gauge longer-term performance.",
        size: "large"
    },
    [YIELD]: {
        label: "Yield",
        tip: "The income the fund has returned to investors, expressed as a percentage. You can use this to gauge the potential income you could earn from the fund.",
        size: "large"
    },
    [INSTRUMENT_STRUCTURE]: {
        label: "Type",
        tip: "The type of fund. Two of the most popular fund types are open-ended investment companies (OEICs) and unit trusts.",
        size: "large"
    },
    [REPLICATION_METHOD]: {
        label: "Replication method",
        tip: "How the ETF replicates its index – either fully, partially or synthetically."
    },
    [INCOME_FREQUENCY]: {
        label: "Income frequency",
        tip: "The frequency income is paid out from the fund to you."
    }
}

export default function SummaryInfo({quote}) {

    const [quoteColumns, setQuoteColumns] = useState(quoteColumnsInitial);

    useEffect(() => {
        if (quote.dividendExDate && quote.dividendPayDate) {
            const columns = {...quoteColumns};
            columns.CEF[0][0].push(DIVIDEND_EXDATE);
            columns.CEF[0][1].push(DIVIDEND_PAYDATE);
            columns.Equity[1][0].push(DIVIDEND_EXDATE);
            columns.Equity[1][1].push(DIVIDEND_PAYDATE);
            setQuoteColumns(columns);
        }
    }, [quote]);

    const formatFrequency = (value) => {
        if (priceFrequencyMapping.hasOwnProperty(value)) {
            return priceFrequencyMapping[value];
        }

        return value;
    }

    const renderColumns = () => {

        const columns = [];

		if (quoteColumns.hasOwnProperty(quote.type)) {
            quoteColumns[quote.type].map(halfConf => {
                let halves = [];
                halfConf.map(columnConf => {
                    let cells = [];
                    columnConf.map(propName => {
                        let cell = {
                            name: propName,
                            label: labelsTips[propName].label,
                            tip: labelsTips[propName].tip,
                            value: propName === PRICING_FREQUENCY ? formatFrequency(quote[propName]) : quote[propName],
                            size: labelsTips[propName].size
                        };
                        
                        switch (quote.type) {
                            case FUND:
                                if (labelsTips[propName].fundTip) {
                                    cell.tip = labelsTips[propName].fundTip;
                                }
                                break;
                            case ETF:
                                if (labelsTips[propName].etfTip) {
                                    cell.tip = labelsTips[propName].etfTip;
                                }
                                break;
                            case CEF:
                                if (labelsTips[propName].cefTip) {
                                    cell.tip = labelsTips[propName].cefTip;
                                }
                                break;
                        }

                        cells.push(cell);
                    });
                    let column = {cells: cells};
                    halves.push(column);
                });
                columns.push(halves)
            });
        }
        
        let halvesJsx = [];

		columns.forEach((half, i) => {
			halvesJsx.push(createColumn(half, i))
		})

        return <>
            {halvesJsx}
        </>
    }

    const createColumn = (half, key) => {
		return <div key={key} className="ajb-col-6 ajb-col-4-breakpoint">
            <div className="ajb-grid-row">
                {
                    half.map((column, index) => (
                        <div key={index} className="ajb-col-6 ajb-col-8-breakpoint">
                            {
                                column.cells.map(cell => (
                                    <div key={cell.label} className="ajb-grid-row stock-chip-info">
                                        <p className="margin-bottom-0 grey-500-text inline-block" data-testid={cell.name}>
                                            {cell.label}&nbsp;
                                            <a aria-label={cell.tip} data-balloon-length={cell.size ? cell.size : null} data-balloon-pos="up" data-testid={`${cell.name}TooltipText`} className="hide-sm">
                                                <i className="fas fa-question-circle tooltipped" data-testid={`${cell.name}Tooltip`}/>
                                            </a>
                                        </p>
                                        <p className="font-weight-bold h6 margin-0" data-testid={`${cell.name}Value`}>{cell.value ?? '-'}</p>
                                        <hr/>
                                    </div>
                                ))
                            }
                        </div>
                    ))
                }
            </div>
        </div>
    }

    return <div className="ajb-col-collapse">
        <div className="card z-depth-0 margin-0 transparent-bg break-word">
            <div className="card-content padding-top-10-sm padding-right-0-sm padding-bottom-30-sm padding-left-0-sm">
                <div className="ajb-grid-row">
                    <div className="ajb-col-collapse">
                        {renderColumns()}
                    </div>
                </div>
            </div>
        </div>
    </div>
}